@use '@/styles/tokens' as *;

.card {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 272px;
  padding: var(--space-6);
  background: var(--surface-card);
  border: 1px solid var(--border-subtle);
  border-radius: 24px; /* Figma rounded-3xl */
  box-shadow: var(--shadow-tab);
  overflow: hidden;
  cursor: pointer;
  transition:
    box-shadow 0.15s ease,
    border-color 0.15s ease,
    transform 0.15s ease;

  &:hover {
    transform: translateY(-2px);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }
}

/* Cards with a Figma background image (`.cardBg`) supply their own surface —
   the app-chrome background/border would otherwise double up as a visible
   seam around the image's rounded corners. They still lift on hover
   (inherited `transform` above). */
.hasBg {
  background: none;
  border-color: transparent;
  box-shadow: none;
}

/* Legacy wash for running devices with no power control (Diesel pumps, sensors) —
   they have no button/notch, so they keep the plain CSS gradient instead of an image. */
.cardRunning {
  background:
    radial-gradient(
      120% 80% at 80% 100%,
      color-mix(in srgb, $color-accent1-500 28%, transparent) 0%,
      transparent 60%
    ),
    var(--surface-card);
}

/* Figma background image (lime wash for running / neutral for stopped, with a
   baked-in notch the power button nests into). Only rendered for devices with a
   power control; sits behind `.content` via DOM order + `.content`'s z-index. */
.cardBg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: 100% 100%;
  pointer-events: none;
}

.content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.disabled {
  cursor: default;
  background: var(--surface-muted);

  &:hover {
    box-shadow: var(--shadow-tab);
    border-color: var(--border-subtle);
  }

  /* Fade the artwork + supporting text, but keep the status pill vivid. */
  .image {
    filter: grayscale(0.6);
    opacity: 0.5;
  }

  .type,
  .name,
  .time {
    opacity: 0.6;
  }
}

.head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
}

.type {
  font-size: 13px;
  color: var(--text-muted);
}

.time {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--text-muted);
}

.name {
  margin: var(--space-1) 0 0;
  font-family: #{$font-family-heading};
  font-size: 24px;
  font-weight: var(--font-weight-regular);
  letter-spacing: -0.02em;
  color: var(--text-primary);
  line-height: 1.15;
}

.media {
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 150px;
  margin-top: var(--space-2);
}

.image {
  max-width: 78%;
  max-height: 170px;
  object-fit: contain;
}

.pill {
  position: absolute;
  bottom: -0.5rem;
  left: 50%;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
  border-radius: var(--radius-pill);
}

.toneRunning {
  color: $color-info-700;
  background: $color-info-100;
}

.toneStopped {
  color: var(--text-inverse);
  background: $color-green-primary-500;
}

.toneOffline {
  color: var(--text-secondary);
  background: var(--surface-card);
  border: 1px solid var(--border-default);
}

.tonePending {
  color: $color-brand-950;
  background: $color-warning-500;
}

/* In-flight command pill (story 101849) — the soft "queued" yellow from the Figma. */
.toneQueued {
  color: $color-brand-950;
  background: #efe96b;
}

.action {
  position: absolute;
  right: 38px;
  bottom: -20px;
}

/* Unconfigured "Setup Required" card (story 101971). */
.setupBadge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: var(--font-weight-medium);
  color: $color-brand-950;
  background: $color-warning-500;
  border-radius: var(--radius-pill);
}

.finishSetup {
  position: absolute;
  bottom: var(--space-2);
  left: 50%;
  transform: translateX(-50%);
  padding: 9px 22px;
  font-size: 13px;
  font-weight: var(--font-weight-medium);
  color: var(--text-inverse);
  background: $color-brand-950;
  border: 0;
  border-radius: var(--radius-pill);
  cursor: pointer;
  transition: background 0.12s ease;

  &:hover {
    background: #000;
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }
}
